import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ADD_ROW, BUY, BUY_SIDE, ConvertOrderAndTradeStatus,  
   DELETE_ROW, FormatWithDecimals, JSON_KEY_INSTRUMENT, JSON_KEY_ORDER_AMEND_NUMBER, 
   JSON_KEY_ORDER_NUMBER, JSON_KEY_PRICE, JSON_KEY_STATUS, JSON_KEY_TIME, JSON_KEY_TRADE_AMEND_NUMBER, 
   JSON_KEY_TRADE_NUMBER, JSON_KEY_TRADING_ACCOUNT, JSON_KEY_VERB, SELL, TABLE_HEADER_AMEND_NUMBER,   
   TABLE_HEADER_INSTRUMENT, TABLE_HEADER_ORDER_NUMBER, TABLE_HEADER_PRICE, TABLE_HEADER_STATUS, TABLE_HEADER_TIME, 
   TABLE_HEADER_TRADE_NUMBER, TABLE_HEADER_TRADING_ACCOUNT, TABLE_HEADER_VERB, UPDATE_ROW, 
   TABLE_HEADER_ORDER_AMEND_NUMBER, JSON_KEY_AGRESSOR, TABLE_HEADER_AGGRESSOR, JSON_KEY_QTY, TABLE_HEADER_QTY,
   ConvertVerb,
   JSON_KEY_VALUE,
   TABLE_HEADER_VALUE,
   Get10Power,
   verbColor,
   DELETE_TABLE} 
from '../../common/common';
import { useSelector } from 'react-redux';
import { selectTableData, selectTradesRequest } from '../../redux/globalsSlice';
import VirtualizedTablesTemplate from './VirtualizedTablesTemplate';
import { selectInstruments, selectTradingUsers } from '../../redux/dropDownSlice';
import TradeRequestForm from '../forms/trade_request.';
import { Modal } from 'react-bootstrap';

function tradeNumberColor(rawStatus) {
   const s = String(rawStatus ?? '').trim().toUpperCase();
   // Trades are almost always matched once they exist.
   // If they're ever in a "failed"/"rejected" state, red; otherwise the default blue.
   if (s === 'FAILED' || s === 'REJECTED') return '#e74c3c';
   return '#5FA8F5';
}

function tradeStatusColor(rawStatus) {
   const s = String(rawStatus ?? '').trim().toUpperCase();
   switch (s) {
      case 'MATCHED':
         return '#4caf50';   // green
      case 'FAILED':
      case 'REJECTED':
         return '#e74c3c';   // red
      default:
         return '#8F96A0';   // grey
   }
}

const UsersTrades2 = () => {
   const instrumentsTableData = useSelector(selectTableData);
   const instrumentsTableDataRef = useRef({});
   const tradesRequestForm = useSelector(selectTradesRequest);
   const tradeRequestFormRef = useRef(tradesRequestForm);
   const dropDownInstrumentsData = useSelector(selectInstruments);
   const dropDownTradingUsersData = useSelector(selectTradingUsers);
   const [showTradesRequestForm, setShowTradesRequestForm] = useState(null);
      
   // RAW DATA STORE
   const rawDataStore = useRef(new Map());
   
   // CRITICAL FIX: Track which IDs changed in this batch
   const changedIdsRef = useRef(new Set());
   const messageQueueRef = useRef([]);
   const flushTimerRef = useRef(null);
   const isMountedRef = useRef(true);

   useEffect(() => {
      instrumentsTableDataRef.current = instrumentsTableData;
   }, [instrumentsTableData]);

   useEffect(() => {
      isMountedRef.current = true;
      return () => {
         isMountedRef.current = false;
         if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
         }
      };
   }, []);

   useEffect(() => {
      tradeRequestFormRef.current = tradesRequestForm;
   }, [tradesRequestForm]);

   const handleSingleClick = useCallback((rowData) => {
      // console.log(`[UsersTradesTable] Row Clicked:`, rowData);
   }, []);

   const handleEmptyTableClick = useCallback((rowData) => {
      // console.log(`[UsersTradesTable] Empty Row Double Clicked:`, rowData, ', showOrdersRequestForm:', orderRequestFormRef.current);
      if (tradeRequestFormRef.current) {
         setShowTradesRequestForm(true);
      }
   }, []);
   
   const handleCloseTradesRequestForm = () => {
      setShowTradesRequestForm(false);
   };

   const handleDoubleClick = useCallback((rowData) => {
      // console.log(`[UsersTradesTable] Row Double Clicked:`, rowData);
      if (tradeRequestFormRef.current) {
         setShowTradesRequestForm(true);
      }
   }, []);

   const columnStyling = useMemo(() => ({
      verb: (rowData) => ({
         color: verbColor(rowData.verb),
      }),
      agr: (rowData) => ({
         color: verbColor(rowData.agr),
      }),
      [JSON_KEY_TRADE_NUMBER]: (rowData) => ({
         color: tradeNumberColor(rowData.status),
      }),
      [JSON_KEY_STATUS]: (rowData) => ({
         color: tradeStatusColor(rowData.status),
      }),
   }), []);

   const processRow = useCallback((rawData) => {
      const { instr, verb, status, agr } = rawData;
      const instrumentRow = instrumentsTableDataRef.current[instr];
      const price_dec = instrumentRow?.price_dec ?? 0;
      const qty_dec = instrumentRow?.qty_dec ?? 0;
      const value = rawData.qty * rawData.price;

      return {
         ...rawData,
         id: rawData.id,
         price: FormatWithDecimals(rawData.price, price_dec),
         qty: FormatWithDecimals(rawData.qty, qty_dec),
         val: FormatWithDecimals(value / Get10Power(qty_dec), price_dec),
         verb: verb === BUY_SIDE ? BUY : SELL,
         status: ConvertOrderAndTradeStatus(status),
         agr: ConvertVerb(agr),
      };
   }, []);

   // Only queue messages first, then process changed rows only
   const handleEvents = useCallback((details, setLocalTableData) => {
      const { type, data } = details;
      
      messageQueueRef.current.push({ type, data });

      if (!flushTimerRef.current && isMountedRef.current) {
         flushTimerRef.current = setTimeout(() => {
            // const startTime = performance.now();
            // const queuedMessages = messageQueueRef.current.length;
            
            // console.log(`[UsersTradesTable] Processing batch: ${queuedMessages} queued messages`);

            changedIdsRef.current.clear();
            
            for (const { type, data } of messageQueueRef.current) {
               const { t_num, ta_num, verb } = data;
               const id = `${t_num}-${ta_num}-${verb}`;

               switch (type) {
                  case ADD_ROW:
                     if (!rawDataStore.current.has(id)) {
                        rawDataStore.current.set(id, { ...data, id });
                        changedIdsRef.current.add(id);
                     }
                     break;

                  case UPDATE_ROW:
                     rawDataStore.current.set(id, { ...data, id });
                     changedIdsRef.current.add(id);
                     break;

                  case DELETE_ROW:
                     if (rawDataStore.current.has(id)) {
                        rawDataStore.current.delete(id);
                        changedIdsRef.current.add(id);
                     }
                     break;

                  case DELETE_TABLE:
                     // console.log('[UserstradesTable] DELETE_TABLE event received, clearing all data');
                     rawDataStore.current.clear();
                     setLocalTableData([]);                     
                     break;
                  
                  default:
                     console.warn(`[UsersTradesTable] Unknown event type: ${type}`);
                     break;
               }
            }

            // Clear queue
            messageQueueRef.current = [];
            
            // const processingStart = performance.now();
            
            const changedIds = Array.from(changedIdsRef.current);
            const processedChanges = changedIds
               .filter(id => rawDataStore.current.has(id))
               .map(id => processRow(rawDataStore.current.get(id)));
            
            // const processingTime = performance.now() - processingStart;
            
            setLocalTableData(prev => {
               const updated = new Map(prev.map(r => [r.id, r]));
               
               changedIds.forEach(id => {
                  if (!rawDataStore.current.has(id)) {
                     updated.delete(id);
                  }
               });
               
               processedChanges.forEach(row => {
                  updated.set(row.id, row);
               });
               
               return Array.from(updated.values());
            });
            
            // const totalTime = performance.now() - startTime;
            // console.log(`[UsersTradesTable] Batch complete: ${queuedMessages} messages → ${changedIds.length} changed → ${rawDataStore.current.size} total rows in ${processingTime.toFixed(0)}ms (total: ${totalTime.toFixed(0)}ms)`);
            
            changedIdsRef.current.clear();
            flushTimerRef.current = null;
         }, 500);
      }
   }, [processRow]);

   const columns = useMemo(() => [
      { Header: TABLE_HEADER_TRADE_NUMBER, accessor: JSON_KEY_TRADE_NUMBER },
      { Header: TABLE_HEADER_AMEND_NUMBER, accessor: JSON_KEY_TRADE_AMEND_NUMBER },
      { Header: TABLE_HEADER_TIME, accessor: JSON_KEY_TIME },
      { Header: TABLE_HEADER_INSTRUMENT, accessor: JSON_KEY_INSTRUMENT },
      { Header: TABLE_HEADER_PRICE, accessor: JSON_KEY_PRICE },
      { Header: TABLE_HEADER_QTY, accessor: JSON_KEY_QTY },
      { Header: TABLE_HEADER_VALUE, accessor: JSON_KEY_VALUE },
      { Header: TABLE_HEADER_TRADING_ACCOUNT, accessor: JSON_KEY_TRADING_ACCOUNT },
      { Header: TABLE_HEADER_VERB, accessor: JSON_KEY_VERB },
      { Header: TABLE_HEADER_ORDER_NUMBER, accessor: JSON_KEY_ORDER_NUMBER },
      { Header: TABLE_HEADER_ORDER_AMEND_NUMBER, accessor: JSON_KEY_ORDER_AMEND_NUMBER },
      { Header: TABLE_HEADER_AGGRESSOR, accessor: JSON_KEY_AGRESSOR },
      { Header: TABLE_HEADER_STATUS, accessor: JSON_KEY_STATUS },
   ], []);

   return (
      <>
         <VirtualizedTablesTemplate
            columns={columns}
            eventHandlers={handleEvents}
            tableName="UsersTradesTable"
            onRowClick={handleSingleClick}
            onRowDoubleClick={handleDoubleClick}
            onEmptyTableClick={handleEmptyTableClick}
            highlightOnClick={true}
            groupBy="t_num"
            subGroupBy="ta_num" 
            enableSearch={true}
            columnStyling={columnStyling}
            defaultSortBy={[
               { id: JSON_KEY_TRADE_NUMBER, desc: false },
            ]}
            enableBatching={false}
         />

         <Modal show={showTradesRequestForm} onHide={handleCloseTradesRequestForm}>
            {showTradesRequestForm && (<TradeRequestForm onClose={handleCloseTradesRequestForm} usersOptions={dropDownTradingUsersData} instrumentsOptions={dropDownInstrumentsData} />)}
         </Modal>
      </>
   );
};

export default UsersTrades2;